/**
 * Detección determinista de facturas de abono / rectificativas a partir del
 * texto del propio documento, como segunda comprobación independiente de la IA.
 *
 * - Indicios FUERTES → marcan el documento como abono automáticamente.
 * - Indicios DÉBILES → solo generan un aviso en el asistente virtual
 *   (evitan falsos positivos del tipo "abono mensual", "cuota de abonado"…).
 */

export interface CreditNoteHints {
  /** Indicio inequívoco: título de rectificativa/abono o total negativo. */
  strong: boolean;
  /** Indicio dudoso: la palabra "abono" suelta, paréntesis en importes… */
  weak: boolean;
  /** Textos concretos encontrados, para mostrarlos en los avisos. */
  matched: string[];
}

const normalize = (value: string): string =>
  (value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/** Títulos que solo aparecen en abonos/rectificativas. */
const STRONG_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /factura\s+rectificativa/, label: "Factura rectificativa" },
  { re: /\bfra\.?\s*rectificativa/, label: "Fra. rectificativa" },
  { re: /factura\s+de\s+abono/, label: "Factura de abono" },
  { re: /nota\s+de\s+abono/, label: "Nota de abono" },
  { re: /nota\s+de\s+credito/, label: "Nota de crédito" },
  { re: /\bcredit\s+note\b/, label: "Credit note" },
  { re: /\babono\s+de\s+factura\b/, label: "Abono de factura" },
];

const WEAK_PATTERNS: { re: RegExp; label: string }[] = [
  { re: /\babonos?\b/, label: '"Abono" en el documento' },
  { re: /\brectificativa\b/, label: '"Rectificativa" en el documento' },
  { re: /\bdevoluci[oó]n\b/, label: '"Devolución" en el documento' },
];

const AMOUNT = String.raw`\d{1,3}(?:[.\s]\d{3})*(?:,\d{1,2})?|\d+(?:[.,]\d{1,2})?`;

/** Importes con signo delante, detrás o entre paréntesis. */
const NEGATIVE_AMOUNT_RES: { re: RegExp; label: string }[] = [
  { re: new RegExp(String.raw`-\s*(?:${AMOUNT})\s*(?:€|eur)`, "i"), label: "Importe negativo" },
  { re: new RegExp(String.raw`(?:€|eur)\s*-\s*(?:${AMOUNT})`, "i"), label: "Importe negativo" },
  { re: new RegExp(String.raw`(?:${AMOUNT})\s*(?:€|eur)?\s*-\s*$`, "i"), label: "Importe con signo detrás" },
  { re: new RegExp(String.raw`\(\s*(?:${AMOUNT})\s*(?:€|eur)?\s*\)`, "i"), label: "Importe entre paréntesis" },
];

/** Líneas donde interesa buscar el signo: total, base, importe… */
const TOTAL_LINE = /(total|base\s+imponible|importe|suma)/;

/**
 * Analiza el texto del documento (y el tipo detectado por la IA) y devuelve
 * los indicios de abono/rectificativa encontrados.
 */
export const detectCreditNoteHints = (text?: string, documentType?: string): CreditNoteHints => {
  const haystack = normalize(`${documentType || ""}\n${text || ""}`);
  const matched: string[] = [];
  let strong = false;
  let weak = false;

  if (!haystack.trim()) return { strong, weak, matched };

  // Los títulos solo cuentan como indicio fuerte en la cabecera del documento
  // (tipo detectado + primeras líneas); en pies legales solo es un aviso.
  const header = haystack.split(/\n+/).slice(0, 40).join("\n");
  for (const { re, label } of STRONG_PATTERNS) {
    if (re.test(header)) {
      strong = true;
      if (!matched.includes(label)) matched.push(label);
    } else if (re.test(haystack)) {
      weak = true;
      if (!matched.includes(label)) matched.push(label);
    }
  }

  // Importes negativos: solo en líneas de total/base/importe, para no
  // confundir guiones de números de factura o fechas.
  const lines = haystack.split(/\n+/);
  for (const line of lines) {
    if (!TOTAL_LINE.test(line)) continue;
    for (const { re, label } of NEGATIVE_AMOUNT_RES) {
      if (re.test(line)) {
        // Solo el total final negativo es inequívoco; descuentos, abonos
        // parciales o totales de línea quedan como aviso.
        const isFinalTotal =
          /^\s*(importe\s+total|total)(\s+(neto\s+)?(factura|fra\.?|a\s+pagar|documento|importe|eur|€|liquido))?\b/.test(line) &&
          !/(descuento|dto|bonificacion|rappel|anticipo|entrega|a\s+cuenta|linea|base|iva|cuota|bruto)/.test(line);
        if (isFinalTotal) strong = true;
        else weak = true;
        if (!matched.includes(label)) matched.push(label);
        break;
      }
    }
  }

  if (!strong) {
    for (const { re, label } of WEAK_PATTERNS) {
      if (re.test(haystack)) {
        weak = true;
        if (!matched.includes(label)) matched.push(label);
      }
    }
  }

  return { strong, weak, matched };
};
